import { useState } from 'react';
import Button from './Button';
import { login } from '../services/authService';
import validateSmartCotiForm from '../utils/validateSmartCotiForm';
import '../styles/components/QuotationForm.scss';

const API_URL = process.env.REACT_APP_API_URL;

const SmartCotiForm = ({ onBack }) => {
  const [qualified, setQualified] = useState(null); // null | true | false

  const [formData, setFormData] = useState({
    requiredQuantity: '',
    maxUnitPrice: '',
    requiredProduct: '',
    company: '',
    name: '',
    phone: '',
    email: '',
    note: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'phone') {
      formattedValue = value.replace(/\D/g, ''); 
    } else if (name === 'maxUnitPrice' || name === 'requiredQuantity') {
      formattedValue = value.replace(/[^\d]/g, ''); // Solo números
    }

    setFormData({ ...formData, [name]: formattedValue });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleMaxPriceBlur = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      maxUnitPrice: prevFormData.maxUnitPrice ? new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
      }).format(Number(prevFormData.maxUnitPrice.replace(/\D/g, ''))) : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateSmartCotiForm(formData);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;

    const sanitizedMaxPrice = parseInt(formData.maxUnitPrice.replace(/[^0-9]/g, ''), 10) || 0;

    try {
      const token = await login();
      const response = await fetch(`${API_URL}/quotation/send-smart-coti`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
        },
        body: JSON.stringify({
          ...formData,
          maxUnitPrice: sanitizedMaxPrice,
          requiredQuantity: parseInt(formData.requiredQuantity, 10),
        })
      });

      if (!response.ok) {
        throw new Error(`Error en el servidor: ${response.statusText}`);
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error al enviar la cotización:', error);
      alert('Hubo un problema al enviar la cotización.');
    }
  };

  if (isSubmitted) {
    return (
      <div className="QuotationForm__success">
        <h3>¡Cotización enviada con éxito!</h3>
        <p>Gracias por tu interés. Nos pondremos en contacto contigo pronto.</p>
        <a
          href="/categorias"
          className="btn btn-primary">
          Volver al catálogo
        </a>
      </div>
    );
  }

  if (qualified === null) {
    return (
      <div className="QuotationForm__gate">
        <div className="QuotationForm__gate-icon">💼</div>
        <h3 className="QuotationForm__gate-title">
          ¿Tu compra supera los <span>$3.000.000</span>?
        </h3>
        <p className="QuotationForm__gate-sub">
          La cotización rápida está disponible para pedidos mayores a este valor.
        </p>
        <div className="QuotationForm__gate-actions">
          <button className="QuotationForm__gate-btn QuotationForm__gate-btn--yes" onClick={() => setQualified(true)}>
            Sí, continuar
          </button>
          <button className="QuotationForm__gate-btn QuotationForm__gate-btn--no" onClick={() => setQualified(false)}>
            No
          </button>
        </div>
      </div>
    );
  }

  if (qualified === false) {
    return (
      <div className="QuotationForm__gate">
        <div className="QuotationForm__gate-icon">👋</div>
        <h3 className="QuotationForm__gate-title">¡Hablemos por WhatsApp!</h3>
        <p className="QuotationForm__gate-sub">
          Para pedidos menores, nuestro equipo te atiende directamente y con la misma rapidez.
        </p>
        <div className="QuotationForm__gate-actions">
          <a
            className="QuotationForm__gate-btn QuotationForm__gate-btn--yes"
            href="https://wa.me/3005571857?text=Hola%2C%20quiero%20cotizar%20productos%20promocionales"
            target="_blank"
            rel="noreferrer"
          >
            Ir a WhatsApp
          </a>
          <button className="QuotationForm__gate-btn QuotationForm__gate-btn--no" onClick={() => setQualified(null)}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="QuotationForm">
      <form onSubmit={handleSubmit}>
        {/* Campos Smart Coti */}
        <label>
          Cantidad requerida:
          <input
            type="text"
            name="requiredQuantity"
            value={formData.requiredQuantity}
            onChange={handleFormChange}
            placeholder="Ejemplo: 500"
          />
          {errors.requiredQuantity && <span className="error">{errors.requiredQuantity}</span>}
        </label>

        <label>
          Precio máximo unidad:
          <input
            type="text"
            name="maxUnitPrice"
            value={formData.maxUnitPrice}
            onChange={handleFormChange}
            onBlur={handleMaxPriceBlur}
            placeholder="Ejemplo: 15000"
          />
          {errors.maxUnitPrice && <span className="error">{errors.maxUnitPrice}</span>}
        </label>

        <label>
          Producto requerido:
          <textarea
            name="requiredProduct"
            value={formData.requiredProduct}
            onChange={handleFormChange}
            placeholder="Ejemplo: Necesito USB personalizadas de 16GB con logo de mi empresa en láser, preferiblemente en colores azul o negro, con empaque individual"
            rows="4"
          />
          {errors.requiredProduct && <span className="error">{errors.requiredProduct}</span>}
        </label>

        <label>
          Empresa:
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleFormChange}
          />
          {errors.company && <span className="error">{errors.company}</span>}
        </label>

        <label>
          Nombre:
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
          />
          {errors.name && <span className="error">{errors.name}</span>}
        </label>

        <label>
          Teléfono:
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleFormChange}
            placeholder="Solo números"
          />
          {errors.phone && <span className="error">{errors.phone}</span>}
        </label>

        <label>
          Correo Electrónico:
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleFormChange}
          />
          {errors.email && <span className="error">{errors.email}</span>}
        </label>

        <label>
          Nota (Opcional):
          <textarea
            name="note"
            value={formData.note}
            onChange={handleFormChange}
            placeholder="Información adicional sobre su solicitud..."
            rows="3"
          ></textarea>
        </label>

        <div className="QuotationForm__buttons">
          <Button text="Atrás" onClick={onBack} />
          <Button text="Enviar Cotización" type="submit" />
        </div>
      </form>
    </div>
  );
};

export default SmartCotiForm;